<template>
  <div class="ledger-flows-container">
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>台账流水查看</span>
      </div>
      <div class="header-actions">
        <el-button size="small" type="primary" icon="el-icon-document" @click="goToDetail">台账详情</el-button>
        <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </div>
    </div>

    <div v-loading="loading" class="content">
      <template v-if="!loading && ledger">
        <el-card class="info-card">
          <div slot="header" class="card-header">台账信息</div>
          <el-descriptions border :column="3">
            <el-descriptions-item label="台账编号">{{ ledger.ledgerNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="合同名称">{{ ledger.contractName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="合同编号">{{ ledger.contractNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="供应商">{{ ledger.supplierUnitName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="需求方">{{ ledger.demandUnitNames || '-' }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ ledger.remark || '无' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <div class="settlement-flows">
          <el-card v-for="group in settlementFlowGroups" :key="group.settlementId" class="settlement-card">
            <div slot="header" class="card-header">
              <span>结算单：{{ group.settlementNumber || group.settlementTitle || ('ID ' + group.settlementId) }}</span>
              <span class="settlement-meta">
                结算单金额：{{ formatNumber(group.settlementAmount) }}；
                已分配：<span class="amount-highlight">{{ formatNumber(group.totalFlowAmount) }}</span>
              </span>
            </div>
            <el-table :data="group.flows" border size="small">
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column label="收付款时间" width="170" align="center">
                <template slot-scope="scope">
                  {{ formatDateTime(scope.row.paymentDate) }}
                </template>
              </el-table-column>
              <el-table-column label="资金类型" width="90" align="center">
                <template slot-scope="scope">
                  {{ scope.row.fundType === 'PAYABLE' ? '付款' : '收款' }}
                </template>
              </el-table-column>
              <el-table-column label="本笔总金额" width="130" align="center">
                <template slot-scope="scope">
                  {{ formatNumber(scope.row.recordAmount) }}
                </template>
              </el-table-column>
              <el-table-column label="分摊到本结算单" width="130" align="center">
                <template slot-scope="scope">
                  <span class="amount-text">{{ formatNumber(scope.row.amount) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="支付方式" width="110" align="center">
                <template slot-scope="scope">
                  {{ scope.row.paymentMethod || '-' }}
                </template>
              </el-table-column>
              <el-table-column label="备注" min-width="140" show-overflow-tooltip>
                <template slot-scope="scope">
                  {{ scope.row.remark || '-' }}
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </div>

        <div v-if="!loading && ledger && settlementFlowGroups.length === 0" class="empty-flows">
          <el-empty description="暂无结算单流水数据，请先在台账详情中添加收付款记录" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { query_fund_ledger_by_id, query_settlements_by_ledger_id } from '@/api/modules/fund-ledger'

export default {
  name: 'LedgerFlows',
  data() {
    return {
      ledgerId: null,
      loading: false,
      ledger: null,
      settlements: []
    }
  },
  computed: {
    // 按结算单分组的流水：每个结算单下为其 ledgerFlows 及对应的收付款记录信息
    settlementFlowGroups() {
      if (!this.ledger || !this.ledger.paymentRecords) return []
      const recordList = this.ledger.paymentRecords || []
      const map = new Map() // settlementId -> { settlementId, settlementNumber, settlementTitle, settlementAmount, flows[] }

      // 先初始化所有有流水的结算单（从 settlements 补全信息）
      recordList.forEach(record => {
        const flows = record.ledgerFlows || []
        flows.forEach(f => {
          if (!map.has(f.settlementId)) {
            const sett = this.settlements.find(s => (s.id || s.settlement_id) === f.settlementId)
            map.set(f.settlementId, {
              settlementId: f.settlementId,
              settlementNumber: f.settlementNumber || (sett && (sett.settlementNumber || sett.settlement_number)),
              settlementTitle: sett && (sett.settlementTitle || sett.settlement_title),
              settlementAmount: sett && sett.amount != null ? sett.amount : null,
              flows: []
            })
          }
        })
      })

      recordList.forEach(record => {
        const flows = record.ledgerFlows || []
        flows.forEach(f => {
          const group = map.get(f.settlementId)
          if (group) {
            group.flows.push({
              paymentDate: record.paymentDate,
              fundType: record.fundType || record.fund_type,
              recordAmount: record.amount,
              amount: f.amount,
              paymentMethod: record.paymentMethod,
              remark: record.remark
            })
          }
        })
      })

      // 计算每个结算单的已分配总额
      const result = []
      map.forEach(g => {
        g.totalFlowAmount = g.flows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
        result.push(g)
      })
      // 按结算单号或 ID 排序
      result.sort((a, b) => {
        const na = (a.settlementNumber || '').toString()
        const nb = (b.settlementNumber || '').toString()
        if (na && nb) return na.localeCompare(nb)
        return (a.settlementId || 0) - (b.settlementId || 0)
      })
      return result
    }
  },
  created() {
    const id = this.$route.params.id
    if (id) {
      this.ledgerId = id
      this.fetchData()
    } else {
      this.$message.warning('缺少台账ID')
      this.goBack()
    }
  },
  methods: {
    async fetchData() {
      if (!this.ledgerId) return
      this.loading = true
      try {
        const [ledgerRes, settlementsRes] = await Promise.all([
          query_fund_ledger_by_id(this.ledgerId),
          query_settlements_by_ledger_id(this.ledgerId)
        ])
        if (ledgerRes.data && ledgerRes.data.success) {
          this.ledger = ledgerRes.data.data
        } else {
          this.$message.error(ledgerRes.data?.message || '获取台账失败')
        }
        if (settlementsRes.data && settlementsRes.data.success) {
          const list = Array.isArray(settlementsRes.data.data) ? settlementsRes.data.data : []
          this.settlements = list.map(s => ({
            id: s.id || s.settlement_id,
            settlement_id: s.id || s.settlement_id,
            settlementNumber: s.settlementNumber || s.settlement_number,
            settlement_number: s.settlementNumber || s.settlement_number,
            settlementTitle: s.settlementTitle || s.settlement_title,
            settlement_title: s.settlementTitle || s.settlement_title,
            settlementDate: s.settlementDate || s.settlement_date,
            amount: s.amount
          }))
        }
      } catch (e) {
        console.error(e)
        this.$message.error('加载失败: ' + (e.response?.data?.message || e.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    formatDateTime(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    formatNumber(val) {
      if (val === undefined || val === null) return '0.00'
      const n = parseFloat(val)
      if (isNaN(n)) return '0.00'
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    goToDetail() {
      this.$router.push({ path: `/finance/finance-detail/${this.ledgerId}` })
    },
    goBack() {
      this.$router.push({ path: '/finance/finance-list' })
    }
  }
}
</script>

<style lang="scss" scoped>
.ledger-flows-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;
  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;
    .title-mark {
      width: 4px;
      height: 18px;
      background: #409EFF;
      margin-right: 10px;
    }
  }
  .header-actions {
    display: flex;
    gap: 10px;
  }
}

.content {
  min-height: 200px;
}

.info-card {
  margin-bottom: 20px;
  .card-header {
    font-size: 16px;
    font-weight: bold;
  }
}

.settlement-flows {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.settlement-card {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 15px;
    font-weight: bold;
    .settlement-meta {
      font-size: 13px;
      font-weight: normal;
      color: #606266;
      .amount-highlight {
        color: #f56c6c;
        font-weight: 600;
      }
    }
  }
}

.amount-text {
  color: #f56c6c;
  font-weight: 600;
}

.empty-flows {
  padding: 40px 20px;
  background: #fff;
  border-radius: 4px;
}
</style>
